import type { Map } from "ol";
import Feature from "ol/Feature";
import LineString from "ol/geom/LineString";
import CircleGeom from "ol/geom/Circle";
import Point from "ol/geom/Point";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { fromLonLat } from "ol/proj";
import Style from "ol/style/Style";
import Stroke from "ol/style/Stroke";
import Fill from "ol/style/Fill";
import CircleStyle from "ol/style/Circle";
import Text from "ol/style/Text";

export type FigurePoint = { longitude: number; latitude: number };

export type FigureLineStyle = {
    mode?: "dashed" | "solid";
    color?: string;
    width?: number;
    label?: {
        text: string;
        color?: string;
        outlineColor?: string;
        outlineWidth?: number;
        font?: string;
        /** Повторять надпись вдоль линии (в пикселях). */
        repeat?: number;
        /** Смещение от линии в пикселях */
        offsetY?: number;
    };
    /**
     * Явное переопределение штриховки.
     * Если не задано:
     * - mode === 'dashed' → [8, 6]
     * - mode === 'solid'  → undefined
     */
    lineDash?: number[];
};

export type FigureCircleStyle = {
    strokeColor?: string;
    strokeWidth?: number;
    fillColor?: string;
    lineDash?: number[];
};

export type MapFigure =
    | {
        type: "line";
        id?: string | number;
        from: FigurePoint;
        to: FigurePoint;
        style?: FigureLineStyle;
        zIndex?: number;
    }
    | {
        type: "circle";
        id?: string | number;
        center: FigurePoint;
        radiusMeters: number;
        style?: FigureCircleStyle;
        zIndex?: number;
    }
    | {
        type: "point";
        id?: string | number;
        position: FigurePoint;
        style?: FigureCircleStyle;
        zIndex?: number;
    };

function isPoint(p: any): p is FigurePoint {
    return p && typeof p.longitude === "number" && typeof p.latitude === "number";
}

function featureForFigure(fig: MapFigure) {
    if (fig.type === "line") {
        const { from, to } = fig;
        if (!isPoint(from) || !isPoint(to)) return undefined;
        const coords = [fromLonLat([from.longitude, from.latitude]), fromLonLat([to.longitude, to.latitude])];
        const geom = new LineString(coords);
        const feature = new Feature({ geometry: geom });
        (feature as any).setId?.(fig.id ?? undefined);

        const cfg = fig.style ?? {};
        const resolvedDash =
            cfg.lineDash ??
            (cfg.mode === "solid" ? undefined : [8, 6]); // dashed by default

        const labelCfg = cfg.label;
        feature.setStyle(
            new Style({
                stroke: new Stroke({
                    color: cfg.color ?? "rgba(0,0,0,0.7)",
                    width: cfg.width ?? 2,
                    lineDash: resolvedDash,
                }),
                text: labelCfg?.text
                    ? new Text({
                        text: labelCfg.text,
                        placement: "line",
                        overflow: true,
                        repeat: labelCfg.repeat,
                        offsetY: labelCfg.offsetY ?? -10,
                        font: labelCfg.font ?? "12px sans-serif",
                        fill: new Fill({ color: labelCfg.color ?? "rgba(0,0,0,0.85)" }),
                        stroke: new Stroke({
                            color: labelCfg.outlineColor ?? "rgba(255,255,255,0.95)",
                            width: labelCfg.outlineWidth ?? 3,
                        }),
                    })
                    : undefined,
            })
        );
        return feature;
    }

    if (fig.type === "circle") {
        const { center, radiusMeters } = fig;
        if (!isPoint(center) || typeof radiusMeters !== "number") return undefined;
        const c = fromLonLat([center.longitude, center.latitude]);
        const geom = new CircleGeom(c, radiusMeters);
        const feature = new Feature({ geometry: geom });
        (feature as any).setId?.(fig.id ?? undefined);

        const cfg = fig.style ?? {};
        feature.setStyle(
            new Style({
                stroke: new Stroke({
                    color: cfg.strokeColor ?? "rgba(0,0,0,0.7)",
                    width: cfg.strokeWidth ?? 2,
                    lineDash: cfg.lineDash,
                }),
                fill: cfg.fillColor ? new Fill({ color: cfg.fillColor }) : undefined,
            })
        );
        return feature;
    }

    if (fig.type === "point") {
        const { position } = fig;
        if (!isPoint(position)) return undefined;
        const c = fromLonLat([position.longitude, position.latitude]);
        const geom = new Point(c);
        const feature = new Feature({ geometry: geom });
        (feature as any).setId?.(fig.id ?? undefined);

        const cfg = fig.style ?? {};
        feature.setStyle(
            new Style({
                image: new CircleStyle({
                    radius: 6,
                    stroke: new Stroke({
                        color: cfg.strokeColor ?? "rgba(0,0,0,0.7)",
                        width: cfg.strokeWidth ?? 2,
                        lineDash: cfg.lineDash,
                    }),
                    fill: cfg.fillColor ? new Fill({ color: cfg.fillColor }) : undefined,
                }),
            })
        );
        return feature;
    }
}

export function activateAddFiguresFunction(figures: MapFigure[] = [], map?: Map) {
    if (!map || !Array.isArray(figures) || figures.length === 0) {
        return { off: () => { /* nothing */ } };
    }

    const features: Feature[] = [];
    const zIndex = Math.max(
        900,
        ...figures
            .map((f: any) => (typeof f?.zIndex === "number" ? f.zIndex : 900))
            .filter((n: any) => typeof n === "number")
    );

    figures.forEach((fig) => {
        try {
            const feature = featureForFigure(fig);
            if (feature) features.push(feature);
        } catch {
            // ignore bad figure
        }
    });

    if (features.length === 0) {
        return { off: () => { /* nothing */ } };
    }

    const source = new VectorSource({ features });
    const layer = new VectorLayer({ source });
    layer.setZIndex(zIndex);
    map.addLayer(layer);

    return {
        off: () => {
            map.removeLayer(layer);
        },
    };
}


